import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

const initialState = {
  purchase_order_approver_list: [],
  purchase_order_approver_detail: null,
  current_values: null,
  active_payload: null,
  loading: false,
  error: null,
};

// Get all Purchase Order Approvers
export const getPurchaseOrderApprovers = createAsyncThunk(
  "purchaseOrderApprover/getAll",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.APPROVER.PURCHASE_ORDER.SLUG}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Get Purchase Order Approver by ID
export const getPurchaseOrderApproverById = createAsyncThunk(
  "purchaseOrderApprover/getById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.APPROVER.PURCHASE_ORDER.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Create Purchase Order Approver
export const createPurchaseOrderApprover = createAsyncThunk(
  "purchaseOrderApprover/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.APPROVER.PURCHASE_ORDER.SLUG, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Update Purchase Order Approver
export const updatePurchaseOrderApprover = createAsyncThunk(
  "purchaseOrderApprover/update",
  async ({ id, data: payload }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.APPROVER.PURCHASE_ORDER.SLUG}/${id}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Delete Purchase Order Approver
export const deletePurchaseOrderApprover = createAsyncThunk(
  "purchaseOrderApprover/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.APPROVER.PURCHASE_ORDER.SLUG}/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const purchaseOrderApproverSlice = createSlice({
  name: "purchaseOrderApprover",
  initialState,
  reducers: {
    setCurrentValues: (state, action) => {
      state.current_values = action.payload;
    },
    clearCurrentValues: (state) => {
      state.current_values = null;
    },
    setActivePayload: (state, action) => {
      state.active_payload = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all Purchase Order Approvers
      .addCase(getPurchaseOrderApprovers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPurchaseOrderApprovers.fulfilled, (state, action) => {
        state.loading = false;
        state.purchase_order_approver_list = action.payload?.data?.list || [];
      })
      .addCase(getPurchaseOrderApprovers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Purchase Order Approver by ID
      .addCase(getPurchaseOrderApproverById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPurchaseOrderApproverById.fulfilled, (state, action) => {
        state.loading = false;
        state.purchase_order_approver_detail = action.payload?.data;
      })
      .addCase(getPurchaseOrderApproverById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Purchase Order Approver
      .addCase(createPurchaseOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPurchaseOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPurchaseOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Purchase Order Approver
      .addCase(updatePurchaseOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePurchaseOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePurchaseOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Purchase Order Approver
      .addCase(deletePurchaseOrderApprover.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePurchaseOrderApprover.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deletePurchaseOrderApprover.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentValues, clearCurrentValues, setActivePayload } = purchaseOrderApproverSlice.actions;

export default purchaseOrderApproverSlice.reducer;
